import { Canvas, useFrame } from '@react-three/fiber';
import { Float, OrbitControls, Text } from '@react-three/drei';
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { Network, Play, Hash } from 'lucide-react';
import * as THREE from 'three';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import {
  listSmpcBenchmarks,
  listSmpcSessions,
  runSmpcDemoRoundtrip,
  submitSmpcShare,
  type SmpcBenchmark,
  type SmpcRoundtripResult,
  type SmpcSession,
} from '@/lib/api';
import { Send } from 'lucide-react';

const BENCHMARKS = [
  { key: 'bfsi_aadhaar_vault_coverage_pct', desc: 'BFSI: % Aadhaar columns in UIDAI Vault', industry: 'BFSI', agg: 'avg' },
  { key: 'healthcare_section_9_violation_rate', desc: 'Healthcare: DPDPA Sec 9 violations / 1k events', industry: 'Healthcare', agg: 'avg' },
  { key: 'fintech_breach_clock_avg_days', desc: 'Fintech: avg days detect → DPB notification', industry: 'Fintech', agg: 'avg' },
  { key: 'cross_industry_break_glass_per_month', desc: 'All: break-glass sessions / month / tenant', industry: 'All', agg: 'avg' },
  { key: 'cross_industry_section_9_block_rate', desc: 'All: Section 9 hard-blocks / 10k inserts', industry: 'All', agg: 'avg' },
  { key: 'cross_industry_mass_export_p95_multiplier', desc: 'All: P95 mass-export multiplier vs baseline', industry: 'All', agg: 'p95' },
  { key: 'ddr_decisions_per_1k_events_p50', desc: 'P50 DDR block-decisions / 1k events', industry: 'All', agg: 'p50' },
  { key: 'bfsi_aa_revocation_rate', desc: 'BFSI: AA consent revocation / consents granted', industry: 'BFSI', agg: 'avg' },
  { key: 'cross_industry_pq_signed_blocks_pct', desc: 'All: % audit blocks PQ-signed (Dilithium)', industry: 'All', agg: 'avg' },
  { key: 'cross_industry_byo_llm_adoption_pct', desc: 'All: % tenants using BYO-LLM Tier 5', industry: 'All', agg: 'avg' },
];

interface LiveBenchmark {
  key: string;
  desc: string;
  industry: string;
  agg: string;
}

function adaptBenchmark(b: SmpcBenchmark): LiveBenchmark {
  const industryLabel: Record<string, string> = {
    bfsi: 'BFSI',
    healthcare: 'Healthcare',
    fintech: 'Fintech',
    cross_industry: 'All',
  };
  return {
    key: b.key,
    desc: b.description,
    industry: industryLabel[b.targetIndustry] ?? b.targetIndustry,
    agg: b.aggregationFn,
  };
}

export function FrontierSmpc() {
  const [liveBenchmarks, setLiveBenchmarks] = useState<LiveBenchmark[] | null>(null);
  const benchmarks = liveBenchmarks ?? BENCHMARKS;
  const [selected, setSelected] = useState(benchmarks[0]!);
  // Wave 5 (2026-05-27) - surface POST /v1/frontier/smpc/shares.
  // Operator picks an open session + share index + paste of the
  // encrypted-share KMS ref, then submits the additive share.
  const [shareSessionId, setShareSessionId] = useState('');
  const [shareIndex, setShareIndex] = useState('1');
  const [shareRef, setShareRef] = useState('');
  const [submittingShare, setSubmittingShare] = useState(false);
  const [shareStatus, setShareStatus] = useState<string | null>(null);
  async function handleSubmitShare(): Promise<void> {
    if (!shareSessionId || !shareRef) {
      setShareStatus('sessionId + encryptedShareRef are required');
      return;
    }
    setSubmittingShare(true);
    setShareStatus(null);
    try {
      const res = await submitSmpcShare({
        sessionId: shareSessionId,
        shareIndex: parseInt(shareIndex, 10),
        encryptedShareRef: shareRef,
      });
      setShareStatus(
        `Share submitted - share ${res.shareIndex} of session ${res.sessionId.slice(0, 8)}… recorded as ${res.shareId.slice(0, 12)}…`,
      );
    } catch (e) {
      setShareStatus(e instanceof Error ? `Submit failed: ${e.message}` : 'Submit failed');
    } finally {
      setSubmittingShare(false);
    }
  }
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState<SmpcRoundtripResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sessions, setSessions] = useState<SmpcSession[]>([]);

  useEffect(() => {
    let cancelled = false;
    listSmpcBenchmarks()
      .then((res) => {
        if (cancelled || !res.benchmarks.length) return;
        const adapted = res.benchmarks.map(adaptBenchmark);
        setLiveBenchmarks(adapted);
        setSelected(adapted[0]!);
      })
      .catch(() => {
        /* keep mocks */
      });
    listSmpcSessions(10)
      .then((res) => {
        if (cancelled) return;
        setSessions(res.sessions);
      })
      .catch(() => {
        /* keep empty */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function runRoundtrip(): Promise<void> {
    setError(null);
    if (!liveBenchmarks) {
      setRunning(!running);
      return;
    }
    setRunning(true);
    try {
      // 5 tenants contributing demo metrics
      const secrets = [100, 200, 300, 400, 500];
      const result = await runSmpcDemoRoundtrip(selected.key, secrets);
      setLastResult(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'SMPC roundtrip failed');
    } finally {
      setTimeout(() => setRunning(false), 3000);
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title="SMPC Cross-Tenant Benchmarks"
        subtitle="Privacy-preserving aggregates over Mersenne-61 field. Each tenant splits its metric into N additive shares; nobody sees per-tenant inputs, only the aggregate."
        badges={[{ label: 'k≥3 anonymity', variant: 'glass' }, { label: '10 benchmarks', variant: 'info' }]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <GlassCard className="p-6 lg:col-span-1">
          <CardHeader className="p-0 mb-3">
            <CardTitle>Benchmark catalogue</CardTitle>
            <CardDescription clamp>Click to select for SMPC roundtrip</CardDescription>
          </CardHeader>
          <div className="space-y-2 max-h-[440px] overflow-y-auto">
            {benchmarks.map((b) => (
              <motion.button
                key={b.key}
                onClick={() => setSelected(b)}
                className={`w-full text-left glass-lite p-3 rounded-md transition-colors ${selected.key === b.key ? 'border-primary glow-low' : ''}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant={b.industry === 'All' ? 'glass' : 'info'} className="text-[9px]">{b.industry}</Badge>
                  <span className="text-[10px] text-muted-foreground">{b.agg}</span>
                </div>
                <code className="text-[10px] font-mono opacity-80">{b.key}</code>
                <p className="text-xs mt-1">{b.desc}</p>
              </motion.button>
            ))}
          </div>
        </GlassCard>

        {/* 3D split/reconstruct viz */}
        <GlassCard className="p-6 lg:col-span-2">
          <CardHeader className="p-0 mb-3 flex-row items-center justify-between">
            <div>
              <CardTitle>SMPC roundtrip · 3D visualization</CardTitle>
              <CardDescription clamp>5 tenants split secrets → distribute shares → reconstruct aggregate</CardDescription>
            </div>
            <Button data-action="disc.frontier_smpc.a02" onClick={runRoundtrip} variant={running ? 'glass' : 'default'} size="sm" disabled={running}>
              <Play className="h-3 w-3" /> {running ? 'Running…' : liveBenchmarks ? 'Run SMPC roundtrip' : 'Animate'}
            </Button>
          </CardHeader>
          <div className="h-[400px] rounded-md overflow-hidden glass-lite">
            <Canvas camera={{ position: [0, 0, 8], fov: 50 }} dpr={[1, 2]}>
              <Suspense fallback={null}>
                <ambientLight intensity={0.5} />
                <directionalLight position={[5, 5, 5]} intensity={1.5} />
                <SMPCScene running={running} />
                <OrbitControls enableZoom={false} enablePan={false} autoRotate autoRotateSpeed={0.6} />
              </Suspense>
            </Canvas>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-[11px] text-center">
            <div className="glass-lite p-2 rounded-md"><div className="text-muted-foreground text-[9px] uppercase">Selected benchmark</div><code className="text-xs">{selected.key.slice(0, 24)}…</code></div>
            <div className="glass-lite p-2 rounded-md"><div className="text-muted-foreground text-[9px] uppercase">Field prime</div><code className="text-xs">2⁶¹ - 1</code></div>
            <div className="glass-lite p-2 rounded-md"><div className="text-muted-foreground text-[9px] uppercase">Aggregation</div><code className="text-xs">{selected.agg}</code></div>
          </div>
        </GlassCard>
      </div>

      {lastResult && (
        <GlassCard className="p-6 mb-4 border-primary glow-low">
          <CardHeader className="p-0 mb-3">
            <CardTitle className="flex items-center gap-2">
              <Hash className="h-4 w-4 text-primary" />
              Live SMPC reveal · just now
            </CardTitle>
            <CardDescription>
              Aggregate over Mersenne-61 field · per-tenant inputs never disclosed · payload hash on-chain
            </CardDescription>
          </CardHeader>
          <div className="grid grid-cols-12 gap-3 items-center glass-lite p-3 rounded-md">
            <div className="col-span-5">
              <code className="text-xs font-mono">{lastResult.benchmarkKey}</code>
              <div className="text-[10px] text-muted-foreground mt-0.5">
                {lastResult.revealed.participantCount} participants · {lastResult.revealed.aggregationFn}
              </div>
            </div>
            <div className="col-span-4">
              <div className="text-[9px] uppercase text-muted-foreground">payload hash</div>
              <code className="text-[10px] font-mono opacity-80">{lastResult.payloadHash.slice(0, 24)}…</code>
            </div>
            <div className="col-span-3 text-right">
              <span className="text-2xl font-display tabular text-primary">{lastResult.revealed.aggregate}</span>
            </div>
          </div>
        </GlassCard>
      )}

      {error && (
        <GlassCard className="p-4 mb-4 border-destructive">
          <p className="text-xs text-destructive">SMPC error: {error}</p>
        </GlassCard>
      )}

      <GlassCard className="p-6">
        <CardHeader className="p-0 mb-3">
          <CardTitle className="flex items-center gap-2">
            <Hash className="h-4 w-4" />
            Recently revealed aggregates
          </CardTitle>
          <CardDescription>Cleartext only of the aggregate; per-tenant inputs never disclosed</CardDescription>
        </CardHeader>
        <div className="space-y-2">
          {sessions.length === 0
            ? RECENT.map((r, i) => (
                <motion.div
                  key={r.key}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="grid grid-cols-12 gap-3 items-center glass-lite p-3 rounded-md"
                >
                  <div className="col-span-6">
                    <code className="text-xs font-mono">{r.key}</code>
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      {r.participants} participants · revealed {r.when}
                    </div>
                  </div>
                  <div className="col-span-3">
                    <Badge variant="info" className="text-[9px]">
                      {r.agg}
                    </Badge>
                  </div>
                  <div className="col-span-3 text-right">
                    <span className="text-lg font-display tabular">{r.value}</span>
                  </div>
                </motion.div>
              ))
            : sessions.map((s, i) => {
                const revealedAggregate =
                  s.revealedResultJson && typeof s.revealedResultJson === 'object'
                    ? String((s.revealedResultJson as Record<string, unknown>).aggregate ?? '-')
                    : '-';
                const opened = new Date(s.openedAt);
                return (
                  <motion.div
                    key={s.id}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="grid grid-cols-12 gap-3 items-center glass-lite p-3 rounded-md"
                  >
                    <div className="col-span-6">
                      <code className="text-xs font-mono">{s.benchmarkKey}</code>
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        {s.actualParticipants} participants · {s.status} · opened {opened.toLocaleDateString()}
                      </div>
                    </div>
                    <div className="col-span-3">
                      <Badge variant="info" className="text-[9px]">
                        {s.aggregationFn}
                      </Badge>
                    </div>
                    <div className="col-span-3 text-right">
                      <span className="text-lg font-display tabular">{revealedAggregate}</span>
                    </div>
                  </motion.div>
                );
              })}
        </div>
      </GlassCard>

      {/* Wave 5 (2026-05-27) - submit additive share to an open session */}
      <GlassCard className="p-6 mt-4">
        <CardHeader className="p-0 mb-3">
          <CardTitle className="flex items-center gap-2">
            <Send className="h-4 w-4 text-primary" /> Submit additive share (advanced)
          </CardTitle>
          <CardDescription>
            Contribute an encrypted additive secret-share to an open SMPC session. Operator
            UI for the underlying <code className="font-mono text-[10px]">POST /v1/frontier/smpc/shares</code>.
          </CardDescription>
        </CardHeader>
        <div className="grid grid-cols-12 gap-3 text-xs items-end">
          <div className="col-span-6">
            <div className="text-[10px] uppercase text-muted-foreground mb-1">Session ID (UUID)</div>
            <input
              type="text"
              value={shareSessionId}
              onChange={(e) => setShareSessionId(e.target.value)}
              placeholder="e.g. 7e3c4b22-9a52-4c4f-b3a6-…"
              className="glass-lite w-full px-3 py-1.5 rounded-md text-xs border border-border/40 focus:outline-none focus:border-primary"
            />
          </div>
          <div className="col-span-2">
            <div className="text-[10px] uppercase text-muted-foreground mb-1">Share index</div>
            <input
              type="number"
              min={1}
              value={shareIndex}
              onChange={(e) => setShareIndex(e.target.value)}
              className="glass-lite w-full px-3 py-1.5 rounded-md text-xs border border-border/40 focus:outline-none focus:border-primary"
            />
          </div>
          <div className="col-span-4">
            <Button
              data-action="disc.frontier_smpc.a01"
              className="w-full"
              size="sm"
              onClick={() => void handleSubmitShare()}
              disabled={submittingShare}
            >
              <Send className="h-3.5 w-3.5 mr-1.5" />
              {submittingShare ? 'Submitting…' : 'Submit share'}
            </Button>
          </div>
          <div className="col-span-12">
            <div className="text-[10px] uppercase text-muted-foreground mb-1">Encrypted-share KMS ref</div>
            <input
              type="text"
              value={shareRef}
              onChange={(e) => setShareRef(e.target.value)}
              placeholder="kms://smpc/<sessionId>/share-<N>"
              className="glass-lite w-full px-3 py-1.5 rounded-md text-xs border border-border/40 focus:outline-none focus:border-primary"
            />
          </div>
          {shareStatus && (
            <div className="col-span-12 glass-lite rounded-md p-2 text-[11px] text-muted-foreground mt-1">
              {shareStatus}
            </div>
          )}
        </div>
      </GlassCard>
    </PageContainer>
  );
}

function SMPCScene({ running }: { running: boolean }) {
  const groupRef = useRef<THREE.Group>(null);
  const tenants = 5;
  const radius = 3;
  const positions = useMemo(() =>
    Array.from({ length: tenants }, (_, i) => {
      const a = (i / tenants) * Math.PI * 2;
      return [Math.cos(a) * radius, Math.sin(a) * radius, 0] as const;
    }),
  []);

  useFrame((state, delta) => {
    if (groupRef.current && running) {
      groupRef.current.rotation.z += delta * 0.3;
    }
  });

  return (
    <group ref={groupRef}>
      {/* Center aggregator */}
      <Float speed={2} rotationIntensity={0.5} floatIntensity={0.6}>
        <mesh>
          <icosahedronGeometry args={[0.7, 1]} />
          <meshStandardMaterial
            color="#f15e27"
            metalness={0.6}
            roughness={0.3}
            emissive="#f15e27"
            emissiveIntensity={0.4}
            flatShading
          />
        </mesh>
        <Text position={[0, -1.2, 0]} fontSize={0.25} color="#ffffff" anchorX="center">
          AGGREGATE
        </Text>
      </Float>

      {/* Tenant nodes */}
      {positions.map((p, i) => (
        <group key={i} position={p as unknown as [number, number, number]}>
          <Float speed={1 + i * 0.2} rotationIntensity={0.4}>
            <mesh>
              <sphereGeometry args={[0.4, 32, 32]} />
              <meshStandardMaterial color="#5485c5" metalness={0.5} roughness={0.4} emissive="#182a52" emissiveIntensity={0.3} />
            </mesh>
            <Text position={[0, -0.7, 0]} fontSize={0.2} color="#cccccc" anchorX="center">
              T{i + 1}
            </Text>
          </Float>
          {/* Share lines */}
          <Line start={p as unknown as [number, number, number]} end={[0, 0, 0]} animated={running} delay={i * 0.2} />
        </group>
      ))}
    </group>
  );
}

function Line({ start, end, animated, delay }: { start: [number, number, number]; end: [number, number, number]; animated: boolean; delay: number }) {
  const ref = useRef<THREE.Mesh>(null);
  const dir = useMemo(() => new THREE.Vector3(end[0] - start[0], end[1] - start[1], end[2] - start[2]), [end, start]);
  const length = dir.length();
  const mid = useMemo(() => new THREE.Vector3((start[0] + end[0]) / 2, (start[1] + end[1]) / 2, (start[2] + end[2]) / 2), [start, end]);
  const quat = useMemo(() => {
    const q = new THREE.Quaternion();
    q.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.clone().normalize());
    return q;
  }, [dir]);

  useFrame(({ clock }) => {
    if (ref.current && animated) {
      const t = (clock.getElapsedTime() + delay) % 2;
      const opacity = Math.max(0, 1 - Math.abs(t - 1));
      (ref.current.material as THREE.MeshStandardMaterial).opacity = 0.3 + opacity * 0.5;
    }
  });

  return (
    <mesh ref={ref} position={[mid.x - start[0], mid.y - start[1], mid.z - start[2]]} quaternion={quat}>
      <cylinderGeometry args={[0.015, 0.015, length, 6]} />
      <meshStandardMaterial color="#06b6d4" emissive="#06b6d4" emissiveIntensity={0.6} transparent opacity={0.3} />
    </mesh>
  );
}

const RECENT = [
  { key: 'bfsi_aadhaar_vault_coverage_pct', value: '78.2%', participants: 14, agg: 'avg', when: '2h ago' },
  { key: 'cross_industry_section_9_block_rate', value: '4.7 / 10k', participants: 28, agg: 'avg', when: '6h ago' },
  { key: 'cross_industry_pq_signed_blocks_pct', value: '12.4%', participants: 28, agg: 'avg', when: '1d ago' },
];
